import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  FileText,
  Filter,
  Loader2,
  Search,
  X,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { exportToExcel, exportToPdf } from "#/lib/exportUtils";
import { useAllClaims, useClaimsStats, useMyClaims, useClaim } from "../hooks";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ClaimDocumentViewer } from "../components/ClaimDocumentViewer";
import type { ClaimFilters, ClaimStatus, ClaimType, ExpenseClaimSummary } from "../types";

// ─── constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR   = new Date().getFullYear();
const YEAR_OPTIONS   = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const MONTH_NAMES    = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PAGE_SIZES     = [10, 20, 50] as const;
const ALL_STATUSES: ClaimStatus[] = ["DRAFT","PENDING_APPROVAL","APPROVED","REJECTED","CANCELLED","PAID"];
const ALL_TYPES: ClaimType[] = ["TRAVEL","MEAL","MEDICAL","ACCOMMODATION","OFFICE_SUPPLIES","TRAINING","OTHER"];

// Sentinel year value meaning "All time" — sends NO date params so backend applies no date filter
const YEAR_ALL_TIME = 0;

// ─── helpers ──────────────────────────────────────────────────────────────────

function lastDayOfMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate(); // month is 1-based here
}

function buildDateRange(year: number | "", month: number | "") {
  // All time (sentinel 0) or unset — omit date params entirely so the backend
  // applies no date filter and returns every claim regardless of submittedAt
  if (year === "" || year === YEAR_ALL_TIME) return {};

  if (month !== "") {
    const mm = String(month).padStart(2, "0");
    return {
      dateFrom: `${year}-${mm}-01`,
      dateTo:   `${year}-${mm}-${lastDayOfMonth(year, month)}`,
    };
  }
  return { dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` };
}

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(t: ClaimType) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Was a hand-rolled CSV blob; converted 2026-07-13 to the shared, letterheaded xlsx exporter.
function claimsExportRows(rows: ExpenseClaimSummary[]) {
  const headers = ["Claim No", "Employee", "Type", "Purpose", "Status", "Amount (KES)", "Submitted At"];
  const data = rows.map((c) => [
    c.claimNo, c.employeeName, typeLabel(c.claimType),
    c.purpose ?? "", c.status.replace(/_/g, " "),
    c.totalAmount ?? 0,
    c.submittedAt ? new Date(c.submittedAt).toLocaleDateString() : "",
  ]);
  return { headers, data };
}

function exportClaimsExcel(rows: ExpenseClaimSummary[], title = "claims-report") {
  const { headers, data } = claimsExportRows(rows);
  return exportToExcel("Expense Claims Report", headers, data, `${title}-${new Date().toISOString().slice(0, 10)}`);
}

function exportClaimsPdf(rows: ExpenseClaimSummary[], title = "claims-report") {
  const { headers, data } = claimsExportRows(rows);
  return exportToPdf({
    title: "Expense Claims Report",
    headers,
    rows: data,
    filename: `${title}-${new Date().toISOString().slice(0, 10)}`,
  });
}

// ─── Pagination controls (shared) ─────────────────────────────────────────────

function PaginationBar({
  page, totalPages, total, pageSize,
  onPage, onPageSize,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}) {
  const pages = useMemo(() => {
    const all = Array.from({ length: totalPages }, (_, i) => i);
    if (totalPages <= 7) return all.map((p) => ({ page: p, ellipsis: false }));
    const result: { page: number; ellipsis: boolean }[] = [];
    result.push({ page: 0, ellipsis: false });
    if (page > 3) result.push({ page: -1, ellipsis: true });
    const start = Math.max(1, page - 1);
    const end   = Math.min(totalPages - 2, page + 1);
    for (let i = start; i <= end; i++) result.push({ page: i, ellipsis: false });
    if (page < totalPages - 4) result.push({ page: -2, ellipsis: true });
    result.push({ page: totalPages - 1, ellipsis: false });
    return result;
  }, [page, totalPages]);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-slate-50 px-5 py-3">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>{total} total</span>
        <span>·</span>
        <label className="flex items-center gap-1">
          Per page:
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="ml-1 rounded-lg border bg-white px-2 py-1 text-xs font-semibold text-slate-700"
          >
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1">
        <button
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          className="flex h-7 w-7 items-center justify-center rounded-lg border text-slate-500 hover:bg-slate-100 disabled:opacity-30"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>

        {pages.map((item, i) =>
          item.ellipsis ? (
            <span key={`ellipsis-${i}`} className="px-1 text-xs text-slate-400">…</span>
          ) : (
            <button
              key={item.page}
              onClick={() => onPage(item.page)}
              className={`flex h-7 min-w-[28px] items-center justify-center rounded-lg border px-2 text-xs font-bold transition-colors ${
                page === item.page
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {item.page + 1}
            </button>
          )
        )}

        <button
          disabled={page + 1 >= totalPages}
          onClick={() => onPage(page + 1)}
          className="flex h-7 w-7 items-center justify-center rounded-lg border text-slate-500 hover:bg-slate-100 disabled:opacity-30"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Filter bar (shared between both views) ────────────────────────────────────

type ActiveFilters = {
  year: number | "";      // "" = not applicable (own report uses CURRENT_YEAR default), YEAR_ALL_TIME = all time
  month: number | "";     // 1–12 or ""
  status: ClaimStatus | "";
  claimType: ClaimType | "";
  search: string;
};

function FilterBar({
  filters,
  showYearMonth,
  onChange,
  onClear,
  onExport,
  onExportPdf,
  exportDisabled,
  exporting,
  exportingPdf,
}: {
  filters: ActiveFilters;
  showYearMonth: boolean;
  onChange: (partial: Partial<ActiveFilters>) => void;
  onClear: () => void;
  onExport?: () => void;
  onExportPdf?: () => void;
  exportDisabled?: boolean;
  exporting?: boolean;
  exportingPdf?: boolean;
}) {
  const hasFilters =
    (showYearMonth && (filters.year !== CURRENT_YEAR || filters.month !== "")) ||
    !!filters.status || !!filters.claimType || !!filters.search;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
      <Filter className="h-4 w-4 shrink-0 self-center text-slate-400" />

      {showYearMonth && (
        <>
          {/* Year */}
          <label className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-xs font-bold text-slate-500">
              <Calendar className="h-3 w-3" /> Year
            </span>
            <select
              className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
              value={String(filters.year)}
              onChange={(e) => onChange({ year: e.target.value === "0" ? YEAR_ALL_TIME : Number(e.target.value), month: "" })}
            >
              <option value={String(YEAR_ALL_TIME)}>All time</option>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>

          {/* Month — only meaningful when a specific year is selected */}
          {filters.year !== YEAR_ALL_TIME && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-slate-500">Month</span>
              <select
                className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
                value={String(filters.month)}
                onChange={(e) => onChange({ month: e.target.value ? Number(e.target.value) : "" })}
                disabled={filters.year === ""}
              >
                <option value="">All months</option>
                {MONTH_NAMES.map((name, i) => (
                  <option key={i + 1} value={i + 1}>{name}</option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      {/* Status */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-bold text-slate-500">Status</span>
        <select
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
          value={filters.status}
          onChange={(e) => onChange({ status: e.target.value as ClaimStatus | "" })}
        >
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </label>

      {/* Type */}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-bold text-slate-500">Claim type</span>
        <select
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
          value={filters.claimType}
          onChange={(e) => onChange({ claimType: e.target.value as ClaimType | "" })}
        >
          <option value="">All types</option>
          {ALL_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
      </label>

      {/* Search — realtime */}
      <label className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-xs font-bold text-slate-500">
          <Search className="h-3 w-3" /> Search
        </span>
        <div className="flex items-center gap-1">
          <input
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={showYearMonth ? "Claim No, employee…" : "Claim No, purpose…"}
            className="w-48 rounded-xl border bg-slate-50 px-3 py-1.5 text-sm"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onChange({ search: "" })}
              className="rounded-xl border p-1.5 text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </label>

      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 self-end rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-50"
        >
          <X className="h-3 w-3" /> Clear all
        </button>
      )}

      {(onExport || onExportPdf) && (
        <div className="ml-auto flex items-center gap-2 self-end">
          {onExport && (
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled || exporting}
              className="flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Export Excel
            </button>
          )}
          {onExportPdf && (
            <button
              type="button"
              onClick={onExportPdf}
              disabled={exportDisabled || exportingPdf}
              className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-40"
            >
              {exportingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Export PDF
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Stat chips ───────────────────────────────────────────────────────────────

function ReportCard({ label, value, sub, tone }: {
  label: string; value: string; sub?: string;
  tone: "slate" | "emerald" | "amber" | "indigo";
}) {
  const bg  = { slate: "bg-slate-50 border-slate-200", emerald: "bg-emerald-50 border-emerald-100", amber: "bg-amber-50 border-amber-100", indigo: "bg-indigo-50 border-indigo-100" }[tone];
  const num = { slate: "text-slate-800", emerald: "text-emerald-700", amber: "text-amber-700", indigo: "text-indigo-700" }[tone];
  return (
    <div className={`rounded-2xl border ${bg} px-4 py-4 shadow-sm`}>
      <div className={`text-2xl font-black tracking-tight ${num}`}>{value}</div>
      <div className="mt-1 text-xs font-bold text-slate-500">{label}</div>
      {sub && <div className="mt-0.5 text-xs font-semibold text-slate-400">{sub}</div>}
    </div>
  );
}

// ─── Expandable report row (compact single-line) ──────────────────────────────

function ReportRow({ claim, showEmployee, expanded, onToggle }: {
  claim: ExpenseClaimSummary;
  showEmployee: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detail = useClaim(expanded ? claim.id : undefined);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-slate-50"
      >
        {/* Type icon */}
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-[10px] font-black text-emerald-700">
          {claim.claimType.charAt(0)}
        </div>

        {/* Claim no + status */}
        <span className="shrink-0 text-[11px] font-bold text-slate-400">{claim.claimNo}</span>
        <ClaimStatusBadge status={claim.status} />

        {/* Type · Employee · Purpose — truncated middle */}
        <div className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
          {typeLabel(claim.claimType)}
          {showEmployee && claim.employeeName && (
            <span className="font-normal text-slate-400"> · {claim.employeeName}</span>
          )}
          {claim.purpose && (
            <span className="font-normal text-slate-400"> · {claim.purpose}</span>
          )}
        </div>

        {/* Amount */}
        <span className="shrink-0 text-xs font-black text-slate-800">{fmt(claim.totalAmount ?? 0)}</span>

        {/* Date */}
        {claim.submittedAt && (
          <span className="hidden shrink-0 text-[11px] text-slate-400 sm:inline">{fmtDate(claim.submittedAt)}</span>
        )}

        {/* Chevron */}
        <span className="shrink-0 text-slate-400">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t bg-slate-50 px-5 py-4">
          {detail.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading details…
            </div>
          ) : !detail.data ? (
            <p className="text-sm text-slate-400">Could not load details.</p>
          ) : (
            <div className="space-y-3">
              {detail.data.purpose && (
                <div className="rounded-lg border bg-white px-3 py-2">
                  <span className="text-[10px] font-bold uppercase text-slate-400">Purpose</span>
                  <p className="mt-0.5 text-xs text-slate-700">{detail.data.purpose}</p>
                </div>
              )}

              <div>
                <h4 className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
                  Expense Lines &amp; Documents
                </h4>
                {detail.data.lines.length === 0 ? (
                  <p className="text-xs text-slate-400">No lines added.</p>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      const backendSupportsLineId = detail.data!.documents.some((d) => d.lineId != null);
                      return detail.data!.lines.map((l, idx) => {
                      const lineDocs = backendSupportsLineId
                        ? detail.data!.documents.filter((d) => d.lineId === l.id)
                        : idx === 0 ? detail.data!.documents : [];
                      return (
                        <div key={l.id} className="rounded-xl border bg-white p-3">
                          <div className="flex items-start gap-2">
                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-black text-emerald-700">
                              {idx + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">{l.description}</p>
                                  <p className="text-[10px] text-slate-400">{fmtDate(l.expenseDate)}</p>
                                </div>
                                <span className="shrink-0 text-xs font-black text-emerald-700">
                                  {fmt(l.amount)}
                                </span>
                              </div>
                              {lineDocs.length === 0 ? (
                                <p className="mt-1 text-[10px] italic text-slate-400">No document attached</p>
                              ) : (
                                <div className="mt-1.5 space-y-1">
                                  {lineDocs.map((d) => (
                                    <div key={d.id} className="flex items-center gap-1.5 rounded-lg border bg-slate-50 px-2 py-1">
                                      <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                                      <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700">
                                        {d.originalFileName}
                                      </span>
                                      <ClaimDocumentViewer claimId={claim.id} doc={d} compact />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                      });
                    })()}
                    <div className="flex justify-end text-xs font-black text-slate-700">
                      Total: {fmt(detail.data.lines.reduce((s, l) => s + l.amount, 0))}
                    </div>
                  </div>
                )}
              </div>

              {/* Legacy claim-level docs — only when backend returns lineId */}
              {detail.data.documents.some((d) => d.lineId != null) &&
                detail.data.documents.filter((d) => !d.lineId).length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
                    Additional Documents
                  </h4>
                  {detail.data.documents.filter((d) => !d.lineId).map((d) => (
                    <div key={d.id} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
                        {d.originalFileName}
                      </span>
                      <ClaimDocumentViewer claimId={claim.id} doc={d} compact />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="mt-3 flex justify-end">
            <Link to={`/claims/${claim.id}`} className="text-xs font-bold text-emerald-600 hover:underline">
              Open full claim →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Top stat panels (type + status) ─────────────────────────────────────────

const STATUS_META: { status: ClaimStatus; color: string; bar: string; label: string }[] = [
  { status: "DRAFT",            color: "text-slate-500",   bar: "bg-slate-400",   label: "Draft" },
  { status: "PENDING_APPROVAL", color: "text-amber-600",   bar: "bg-amber-400",   label: "Pending" },
  { status: "APPROVED",         color: "text-emerald-600", bar: "bg-emerald-500", label: "Approved" },
  { status: "REJECTED",         color: "text-rose-600",    bar: "bg-rose-400",    label: "Rejected" },
  { status: "PAID",             color: "text-indigo-600",  bar: "bg-indigo-500",  label: "Paid" },
  { status: "CANCELLED",        color: "text-slate-400",   bar: "bg-slate-300",   label: "Cancelled" },
];

function StatsRow({ claims, periodLabel }: { claims: ExpenseClaimSummary[]; periodLabel: string }) {
  const byType = ALL_TYPES.map((t) => {
    const subset = claims.filter((c) => c.claimType === t);
    return { type: t, count: subset.length, amount: subset.reduce((s, c) => s + (c.totalAmount ?? 0), 0) };
  }).filter((r) => r.count > 0).sort((a, b) => b.amount - a.amount);
  const maxTypeAmt = Math.max(...byType.map((r) => r.amount), 1);
  const total = claims.length;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {/* By Claim Type */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-black text-slate-800">By Claim Type</h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{periodLabel}</span>
        </div>
        {byType.length === 0 ? (
          <p className="text-xs text-slate-400">No data for this period.</p>
        ) : (
          <div className="space-y-2">
            {byType.map((r) => (
              <div key={r.type}>
                <div className="mb-0.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700">{typeLabel(r.type)}</span>
                  <span className="text-[11px] text-slate-400">{r.count} · {fmt(r.amount)}</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(r.amount / maxTypeAmt) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* By Status */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-black text-slate-800">By Status</h3>
        {total === 0 ? (
          <p className="text-xs text-slate-400">No data for this period.</p>
        ) : (
          <div className="space-y-2">
            {STATUS_META.map(({ status, color, bar, label }) => {
              const count = claims.filter((c) => c.status === status).length;
              if (!count) return null;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={status} className="flex items-center gap-2">
                  <span className={`shrink-0 text-xs font-bold ${color}`}>{label}</span>
                  <div className="flex-1 overflow-hidden rounded-full bg-slate-100" style={{ height: 6 }}>
                    <div className={`h-full rounded-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="shrink-0 text-[11px] font-bold text-slate-600">{count}</span>
                  <span className="shrink-0 text-[10px] text-slate-400">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Own claims view (no org-wide permissions) ────────────────────────────────

function OwnClaimsReport() {
  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [active, setActive]     = useState<ActiveFilters>({
    year: YEAR_ALL_TIME, month: "", status: "", claimType: "", search: "",
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  function resetPage() { setPage(0); setExpandedId(null); }
  function updateFilter(partial: Partial<ActiveFilters>) { setActive((prev) => ({ ...prev, ...partial })); resetPage(); }
  function clearFilters() { setActive({ year: YEAR_ALL_TIME, month: "", status: "", claimType: "", search: "" }); resetPage(); }

  async function handleExport() {
    setExporting(true);
    try { await exportClaimsExcel(claims, "my-claims"); } finally { setExporting(false); }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try { await exportClaimsPdf(claims, "my-claims"); } finally { setExportingPdf(false); }
  }

  const dateRange = buildDateRange(active.year, active.month);
  const filters: ClaimFilters = {
    page, size: pageSize,
    ...(active.status    && { status: active.status }),
    ...(active.claimType && { claimType: active.claimType }),
    ...(active.search    && { search: active.search }),
    ...dateRange,
  };

  const { data, isLoading, error: myError } = useMyClaims(filters);
  const statsQuery    = useClaimsStats();
  const stats         = statsQuery.data;
  const claims        = data?.content ?? [];
  const total         = data?.totalElements ?? 0;
  const totalPages    = data?.totalPages ?? 0;
  const myErrorStatus = (myError as { response?: { status?: number } })?.response?.status;

  // Global counts from stats endpoint — not page-limited
  const statTotal       = stats?.totalMine          ?? 0;
  const statTotalAmt    = stats?.totalAmountMine     ?? 0;
  const statPending     = stats?.pendingMine         ?? 0;
  const statPendingAmt  = stats?.pendingAmountMine   ?? 0;
  const statApproved    = stats?.approvedMine        ?? 0;
  const statApprovedAmt = stats?.approvedAmountMine  ?? 0;
  const statPaid        = stats?.paidMine            ?? 0;
  const statPaidAmt     = stats?.paidAmountMine      ?? 0;

  const periodLabel = (() => {
    if (active.year === YEAR_ALL_TIME || active.year === "") return "All time";
    if (active.month !== "") return `${MONTH_NAMES[(active.month as number) - 1]} ${active.year}`;
    return String(active.year);
  })();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">My Claims Report</h1>
        </div>
        <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
          ← Dashboard
        </Link>
      </div>

      {/* Stat chips — sourced from /stats endpoint, not the paged table */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <ReportCard label="Total Claims"    value={String(statTotal)}    sub={fmt(statTotalAmt)}    tone="slate"   />
        <ReportCard label="Pending"         value={String(statPending)}  sub={fmt(statPendingAmt)}  tone="amber"   />
        <ReportCard label="Approved"        value={String(statApproved)} sub={fmt(statApprovedAmt)} tone="emerald" />
        <ReportCard label="Paid"            value={String(statPaid)}     sub={fmt(statPaidAmt)}     tone="indigo"  />
        <ReportCard label="Filtered Claims" value={String(total)}        sub={periodLabel}           tone="slate"   />
      </div>

      {/* Type + Status breakdown at top */}
      <StatsRow claims={claims} periodLabel={periodLabel} />

      {/* Table with filter inline */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        {/* Filter bar just above rows */}
        <div className="border-b px-4 py-3">
          <FilterBar filters={active} showYearMonth onChange={updateFilter} onClear={clearFilters}
            onExport={handleExport} onExportPdf={handleExportPdf}
            exportDisabled={claims.length === 0} exporting={exporting} exportingPdf={exportingPdf} />
        </div>

        <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-2">
          <h2 className="text-sm font-black text-slate-900">
            My Claims
            <span className="ml-1.5 text-xs font-normal text-slate-400">· {total} total</span>
          </h2>
          <span className="text-xs text-slate-400">Click to expand</span>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 px-5 py-8 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : myError ? (
          <div className="px-5 py-8">
            <p className="text-sm font-semibold text-red-600">
              Failed to load claims{myErrorStatus ? ` (HTTP ${myErrorStatus})` : ""}.
            </p>
            <p className="mt-1 text-xs text-slate-400">Check the backend logs or try refreshing.</p>
          </div>
        ) : claims.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-400">No claims match the selected filters.</p>
        ) : (
          <div className="divide-y">
            {claims.map((c) => (
              <ReportRow
                key={c.id} claim={c} showEmployee={false}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              />
            ))}
          </div>
        )}

        <PaginationBar
          page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={(p) => { setPage(p); setExpandedId(null); }}
          onPageSize={(s) => { setPageSize(s); resetPage(); }}
        />
      </div>
    </div>
  );
}

// ─── Main page (org-wide) ─────────────────────────────────────────────────────

export default function ClaimsReportsPage() {
  const { hasRole, hasAnyRole, hasPermission } = useAuth();

  const isAdmin        = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canViewAll     = isAdmin || hasPermission("HR_CLAIM_VIEW_ALL");
  const canViewReports = canViewAll || hasPermission("HR_CLAIM_REPORT_VIEW");

  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [active, setActive]     = useState<ActiveFilters>({
    year: YEAR_ALL_TIME, month: "", status: "", claimType: "", search: "",
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  function resetPage() { setPage(0); setExpandedId(null); }
  function updateFilter(partial: Partial<ActiveFilters>) {
    setActive((prev) => ({ ...prev, ...partial }));
    resetPage();
  }
  function clearFilters() {
    setActive({ year: YEAR_ALL_TIME, month: "", status: "", claimType: "", search: "" });
    resetPage();
  }

  async function handleExport() {
    setExporting(true);
    try { await exportClaimsExcel(claims, canViewAll ? "org-claims" : "my-claims"); } finally { setExporting(false); }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try { await exportClaimsPdf(claims, canViewAll ? "org-claims" : "my-claims"); } finally { setExportingPdf(false); }
  }

  const dateRange = buildDateRange(active.year, active.month);
  const filters: ClaimFilters = {
    page, size: pageSize,
    ...(active.status    && { status: active.status }),
    ...(active.claimType && { claimType: active.claimType }),
    ...(active.search    && { search: active.search }),
    ...dateRange,
  };

  const myQuery    = useMyClaims(!canViewAll ? filters : undefined);
  const adminQuery = useAllClaims(canViewAll ? filters : undefined);
  const statsQuery = useClaimsStats();

  const qData      = canViewAll ? adminQuery : myQuery;
  const claims     = qData.data?.content ?? [];
  const total      = qData.data?.totalElements ?? 0;
  const totalPages = qData.data?.totalPages ?? 0;
  const isLoading  = qData.isLoading;
  const queryError = qData.error;
  const errorStatus = (queryError as { response?: { status?: number } })?.response?.status;

  const stats = statsQuery.data;

  // Global counts from the stats endpoint (not page-limited)
  const statTotal       = canViewAll ? (stats?.totalAll          ?? 0) : (stats?.totalMine          ?? 0);
  const statTotalAmt    = canViewAll ? (stats?.totalAmountAll    ?? 0) : (stats?.totalAmountMine    ?? 0);
  const statPending     = canViewAll ? (stats?.pendingAll        ?? 0) : (stats?.pendingMine        ?? 0);
  const statPendingAmt  = canViewAll ? (stats?.pendingAmountAll  ?? 0) : (stats?.pendingAmountMine  ?? 0);
  const statApproved    = canViewAll ? (stats?.approvedAll       ?? 0) : (stats?.approvedMine       ?? 0);
  const statApprovedAmt = canViewAll ? (stats?.approvedAmountAll ?? 0) : (stats?.approvedAmountMine ?? 0);
  const statPaid        = canViewAll ? (stats?.paidAll           ?? 0) : (stats?.paidMine           ?? 0);
  const statPaidAmt     = canViewAll ? (stats?.paidAmountAll     ?? 0) : (stats?.paidAmountMine     ?? 0);

  if (!canViewReports && !canViewAll) return <OwnClaimsReport />;

  const periodLabel = (() => {
    if (active.year === YEAR_ALL_TIME || active.year === "") return "All time";
    if (active.month !== "") return `${MONTH_NAMES[(active.month as number) - 1]} ${active.year}`;
    return String(active.year);
  })();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Claims Reports</h1>
        </div>
        <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
          ← Dashboard
        </Link>
      </div>

      {/* Stat chips — sourced from /stats endpoint, not the paged table */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <ReportCard label="Total Claims"     value={String(statTotal)}    sub={fmt(statTotalAmt)}    tone="slate"   />
        <ReportCard label="Pending"          value={String(statPending)}  sub={fmt(statPendingAmt)}  tone="amber"   />
        <ReportCard label="Approved"         value={String(statApproved)} sub={fmt(statApprovedAmt)} tone="emerald" />
        <ReportCard label="Paid"             value={String(statPaid)}     sub={fmt(statPaidAmt)}     tone="indigo"  />
        <ReportCard label="Filtered Claims"  value={String(total)}        sub={periodLabel}           tone="slate"   />
      </div>

      {/* By Type + By Status at top */}
      <StatsRow claims={claims} periodLabel={periodLabel} />

      {/* Full-width table with filter just above rows */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        {/* Filter bar */}
        <div className="border-b px-4 py-3">
          <FilterBar filters={active} showYearMonth onChange={updateFilter} onClear={clearFilters}
            onExport={handleExport} onExportPdf={handleExportPdf}
            exportDisabled={claims.length === 0} exporting={exporting} exportingPdf={exportingPdf} />
        </div>

        {/* Table header */}
        <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-2">
          <h2 className="text-sm font-black text-slate-900">
            Claims
            <span className="ml-1.5 text-xs font-normal text-slate-400">· {total} result{total !== 1 ? "s" : ""}</span>
          </h2>
          <span className="text-xs text-slate-400">Click to expand</span>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 px-5 py-8 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : queryError ? (
          <div className="px-5 py-8">
            <p className="text-sm font-semibold text-red-600">
              Failed to load claims{errorStatus ? ` (HTTP ${errorStatus})` : ""}.
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {errorStatus === 403
                ? "You do not have permission to view all claims."
                : "Check the backend logs or try refreshing the page."}
            </p>
          </div>
        ) : claims.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-400">No claims match the selected filters.</p>
        ) : (
          <div className="divide-y">
            {claims.map((c) => (
              <ReportRow
                key={c.id} claim={c} showEmployee={canViewAll}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              />
            ))}
          </div>
        )}

        <PaginationBar
          page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={(p) => { setPage(p); setExpandedId(null); }}
          onPageSize={(s) => { setPageSize(s); resetPage(); }}
        />
      </div>
    </div>
  );
}
